import { makeStyles, Typography } from '@dxb-web-ui-framework/design-system';
import { getClientTimezone, stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { FilterList as FiltersIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TableGridConfig } from '../../../../content-composer/src/components/Generic/types';
import { AccountExtraitTransaction } from '../../domain/Transaction/entities/AccountExtraitTransaction';
import {
  formatAmountCellRenderer,
  stringToDateCellRenderer,
  accountStatmentTransactionTypeCellRenderer,
  viewDetailsButtonRenderer,
} from '../../../../content-composer/src/components/Generic/TableGrid/tableGridCellRenderers/tableGridCellRenderers';
import { formatAmountTransformer } from '../../../../content-composer/src/components/Generic/TableGrid/tableGridMobileListTransformers/tableGridMobileListTransformers';
import { AccountExtraitTransactionFileDownloader } from '../AccountExtraitTransactionFileDownloader';

const useStyles = makeStyles(() => ({
  root: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '20ch',
  },
  counterparty: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '15ch',
  },
  breakWord: {
    width: '15%',
    wordBreak: 'break-all',
  },
}));

const useAccountExtraitTransactionsGridConfig = (
  onToggleDrawer: Function,
  onSelectListItem: (rowData: any) => void,
  onToggleFilterDrawer?: () => void,
): TableGridConfig => {
  const { t } = useTranslation();
  const locale = t('global.settings.date.locale');
  const dateFormat = t('global.settings.date.format');
  const timezone = getClientTimezone();
  const classes = useStyles();

  return {
    gridOptions: {
      ariaTitle: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.AriaTitle'),
      dataTestId: 'Recent-transactions-table',
      noResultsMsg: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.NoTransactionsMessage'),
      padding: 'normal',
      size: 'medium',
      stickyHeader: false,
      collapseToList: true,
    },
    columnDefs: [
      {
        id: 'Creation date',
        field: 'transactionDate',
        label: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.TableHeadingCreationDate'),
        cellRenderer: stringToDateCellRenderer(dateFormat, locale, timezone),
      },
      {
        id: 'Value date',
        field: 'transactionValueDate',
        label: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.TableHeadingValueDate'),
        cellRenderer: stringToDateCellRenderer(dateFormat, locale, timezone),
      },
      {
        id: 'Description',
        field: 'description',
        label: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.TableHeadingDescription'),
        cellRenderer: (rowData: any, columnDef: any) => (
          <Typography variant='subtitle2'>{rowData[columnDef.field]}</Typography>
        ),
      },
      {
        id: 'Counterparty',
        field: 'counterparty',
        label: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.TableHeadingCounterparty'),
        cellProps: {
          classes: { root: classes.counterparty },
        },
      },
      {
        id: 'Payment details',
        field: 'paymentDetails',
        label: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.TableHeadingPaymentDetails'),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      {
        id: 'Transaction type',
        field: 'type',
        label: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.TableHeadingTransactionType'),
        cellRenderer: (rowData: any, columnDef: any) =>
          accountStatmentTransactionTypeCellRenderer(rowData[columnDef.field], t),
      },
      {
        id: 'Amount',
        field: 'amount',
        label: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.TableHeadingAmount'),
        columnHeaderCellProps: { align: 'right' },
        cellProps: { align: 'right', className: classes.breakWord },
        cellRenderer: formatAmountCellRenderer(locale, true),
      },
      {
        id: 'Balance',
        field: 'balance',
        label: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.TableHeadingBalance'),
        columnHeaderCellProps: { align: 'right' },
        cellProps: { align: 'right' },
        cellRenderer: formatAmountCellRenderer(locale, true),
      },
      {
        id: 'View Icon',
        field: 'actions',
        label: '',
        cellProps: { align: 'right' },
        cellRenderer: viewDetailsButtonRenderer(
          onToggleDrawer,
          'transactionId',
          false,
          t('accountsDetailsPage.RecentAccountExtraitTransactions.VisibilityIconBtnTitle'),
          t('accountsDetailsPage.RecentAccountExtraitTransactions.VisibilityIconBtnAriaLabel'),
        ),
      },
    ],
    mobileListConfig: {
      listItemContents: [
        {
          label: 'description',
          value: 'executedDate',
          valueAreaLabel: 'executedDate',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          valueTransformer: (data: AccountExtraitTransaction) =>
            stringToDate({
              date: data.transactionDate,
              formatExp: dateFormat,
              localeName: locale,
              tz: timezone,
            }),
          valueAriaLabelTransformer: (data: AccountExtraitTransaction) =>
            stringToDate({
              date: data.executedDate,
              formatExp: dateFormat,
              localeName: locale,
              tz: timezone,
            }),
        },
        {
          label: 'amount',
          labelColor: 'textPrimary',
          labelSize: 'large',
          labelAlignment: 'right',
          labelTransformer: formatAmountTransformer('amount', 'currency', 2, locale),
        },
      ],
      listItemOnSelect: onSelectListItem,
    },
    // TODO: check permissions first to verify user has access to these actions (see how it is done in account item actions)
    // TODO: add the on click handler methods for the actions
    actions: [
      {
        dataTestId: 'download-transactions',
        variant: 'text',
        color: 'secondary',
        size: 'small',
        disabled: false,
        onClick: () => {},
        Component: AccountExtraitTransactionFileDownloader,
        isIconButton: true,
        title: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.Actions.Download'),
      },
      {
        dataTestId: 'transaction-filters',
        icon: <FiltersIcon color='secondary' />,
        variant: 'text',
        color: 'secondary',
        size: 'large',
        disabled: false,
        onClick: onToggleFilterDrawer ? onToggleFilterDrawer : () => {},
        isIconButton: true,
        title: t('accountExtraitTransactionsPage.AccountExtraitTransactionsTable.Actions.Filters'),
      },
    ],
  };
};

export { useAccountExtraitTransactionsGridConfig };
